import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transferApi, beneficiaryApi, walletApi, utilsApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { useViewMode } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Search, User, Building, Smartphone, Loader2, Check, Delete, Send } from 'lucide-react';

const SendMoneyPage = () => {
  const navigate = useNavigate();
  const { isForcedMobile, isForcedDesktop } = useViewMode();
  const [step, setStep] = useState(1);
  const [transferType, setTransferType] = useState('lekeewallet');
  const [isLoading, setIsLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [banks, setBanks] = useState([]);
  const [mobileProviders, setMobileProviders] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [naturalDesktop, setNaturalDesktop] = useState(window.innerWidth >= 1024);
  
  const [recipientId, setRecipientId] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [pin, setPin] = useState('');

  useEffect(() => {
    const handleResize = () => setNaturalDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = isForcedDesktop ? true : isForcedMobile ? false : naturalDesktop;

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [walletRes, banksRes, providersRes, beneficiariesRes] = await Promise.all([
        walletApi.getWallet(),
        utilsApi.getBanks(),
        utilsApi.getMobileMoneyProviders(),
        beneficiaryApi.getAll(),
      ]);
      setWallet(walletRes.data);
      setBanks(banksRes.data);
      setMobileProviders(providersRes.data);
      setBeneficiaries(beneficiariesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const resolveAccount = async () => {
    if (!selectedBank || accountNumber.length < 10) return;
    
    setIsLoading(true);
    try {
      const res = await beneficiaryApi.resolveAccount(selectedBank, accountNumber);
      setAccountName(res.data.account_name);
      toast.success('Account verified');
    } catch (error) {
      toast.error('Could not verify account');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinInput = (digit) => {
    if (pin.length < 4) {
      setPin(prev => prev + digit);
    }
  };

  const handlePinDelete = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      toast.error('Please enter your 4-digit PIN');
      return;
    }
    
    setIsLoading(true);
    try {
      let response;
      
      if (transferType === 'lekeewallet') {
        response = await transferApi.internal({
          recipient_id: recipientId,
          amount: parseFloat(amount),
          note,
        });
      } else if (transferType === 'bank') {
        response = await transferApi.bank({
          bank_code: selectedBank,
          account_number: accountNumber,
          account_name: accountName,
          amount: parseFloat(amount),
          note,
        });
      } else {
        response = await transferApi.mobileMoney({
          provider: selectedProvider,
          phone_number: phoneNumber,
          amount: parseFloat(amount),
          note,
        });
      }
      
      toast.success('Transfer successful!');
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getFee = () => {
    if (transferType === 'lekeewallet') return 0;
    if (transferType === 'bank') return parseFloat(amount) > 50000 ? 100 : 50;
    return 25;
  };

  const NumberPad = () => (
    <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto mt-6">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, '', 0, 'del'].map((item, idx) => (
        <button
          key={idx}
          onClick={() => item === 'del' ? handlePinDelete() : item !== '' && handlePinInput(String(item))}
          className={`number-pad-btn ${item === '' ? 'invisible' : ''}`}
          disabled={item === ''}
        >
          {item === 'del' ? <Delete className="w-6 h-6" /> : item}
        </button>
      ))}
    </div>
  );

  const canProceedToAmount = () => {
    if (transferType === 'lekeewallet') return !!recipientId;
    if (transferType === 'bank') return !!selectedBank && !!accountNumber && !!accountName;
    return !!selectedProvider && !!phoneNumber;
  };

  // Desktop Layout
  if (isDesktop) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button 
              onClick={() => step > 1 && step < 4 ? setStep(step - 1) : navigate(-1)}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Send Money
              </h1>
              <p className="text-muted-foreground">
                {step === 1 && 'Choose recipient and amount'}
                {step === 2 && 'Review transfer details'}
                {step === 3 && 'Confirm with PIN'}
                {step === 4 && 'Transfer complete'}
              </p>
            </div>
          </div>

          {/* Progress */}
          {step < 4 && (
            <div className="mb-8">
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`h-2 flex-1 rounded-full transition-all ${
                      s <= step ? 'bg-primary' : 'bg-white/10'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Combined Recipient + Amount (Desktop) */}
          {step === 1 && (
            <div className="grid grid-cols-2 gap-8">
              {/* Left: Recipient Selection */}
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-6">Select Recipient</h2>
                
                <Tabs value={transferType} onValueChange={setTransferType}>
                  <TabsList className="grid w-full grid-cols-3 mb-6">
                    <TabsTrigger value="lekeewallet">LekeeWallet</TabsTrigger>
                    <TabsTrigger value="bank">Bank</TabsTrigger>
                    <TabsTrigger value="mobile">Mobile</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="lekeewallet" className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="Enter LekeeWallet ID, phone, or email"
                        value={recipientId}
                        onChange={(e) => setRecipientId(e.target.value)}
                        className="pl-12 h-12 bg-secondary border-white/10"
                        data-testid="recipient-input"
                      />
                    </div>
                    
                    {beneficiaries.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-3">Recent</p>
                        <div className="grid grid-cols-4 gap-3">
                          {beneficiaries.slice(0, 4).map((b, idx) => (
                            <button
                              key={idx}
                              onClick={() => setRecipientId(b.name)}
                              className="flex flex-col items-center gap-2 p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                            >
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                <User className="w-5 h-5 text-primary" />
                              </div>
                              <span className="text-xs text-center truncate w-full">{b.name.split(' ')[0]}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="bank" className="space-y-4">
                    <div className="space-y-2">
                      <Label>Bank</Label>
                      <Select value={selectedBank} onValueChange={setSelectedBank}>
                        <SelectTrigger className="h-12 bg-secondary border-white/10">
                          <SelectValue placeholder="Select bank" />
                        </SelectTrigger>
                        <SelectContent>
                          {banks.map((bank) => (
                            <SelectItem key={bank.code} value={bank.code}>
                              {bank.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        placeholder="Enter 10-digit account number"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        onBlur={resolveAccount}
                        className="h-12 bg-secondary border-white/10"
                        maxLength={10}
                        data-testid="bank-account-input"
                      />
                    </div>
                    
                    {accountName && (
                      <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-500" />
                        <span className="font-medium">{accountName}</span>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="mobile" className="space-y-4">
                    <div className="space-y-2">
                      <Label>Provider</Label>
                      <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                        <SelectTrigger className="h-12 bg-secondary border-white/10">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {mobileProviders.map((provider) => (
                            <SelectItem key={provider.code} value={provider.code}>
                              {provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        placeholder="+234 800 000 0000"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="h-12 bg-secondary border-white/10"
                        data-testid="mobile-phone-input"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Right: Amount Entry */}
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-6">Enter Amount</h2>
                
                {/* Balance */}
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Available: {formatCurrency(wallet?.available_balance)}
                </p>
                
                {/* Amount input */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center">
                    <span className="text-muted-foreground text-3xl">₦</span>
                    <input
                      type="text"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder="0.00"
                      className="text-5xl font-bold bg-transparent border-none outline-none text-center w-64 balance-text"
                      data-testid="amount-input"
                    />
                  </div>
                </div>
                
                {/* Quick amounts */}
                <div className="flex gap-2 justify-center mb-6">
                  {[1000, 5000, 10000, 50000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAmount(String(val))}
                      className="px-4 py-2 bg-secondary rounded-lg text-sm hover:bg-white/10 transition-colors"
                    >
                      ₦{val.toLocaleString()}
                    </button>
                  ))}
                </div>
                
                {/* Fee display */}
                {amount && (
                  <div className="bg-white/5 rounded-xl p-4 mb-6 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amount</span>
                      <span>{formatCurrency(parseFloat(amount) || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Fee</span>
                      <span>{formatCurrency(getFee())}</span>
                    </div>
                    <div className="flex justify-between font-medium pt-2 border-t border-white/10">
                      <span>Total</span>
                      <span>{formatCurrency((parseFloat(amount) || 0) + getFee())}</span>
                    </div>
                  </div>
                )}
                
                {/* Note */}
                <div className="space-y-2 mb-6">
                  <Label>Note (optional)</Label>
                  <Input
                    placeholder="Add a note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="h-12 bg-secondary border-white/10"
                  />
                </div>
                
                <Button
                  onClick={() => setStep(2)}
                  className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
                  disabled={!canProceedToAmount() || !amount || parseFloat(amount) <= 0 || parseFloat(amount) + getFee() > (wallet?.available_balance || 0)}
                  data-testid="continue-to-confirm"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && (
            <div className="max-w-md mx-auto">
              <div className="bg-card border border-white/10 rounded-xl p-8 text-center mb-6">
                <p className="text-muted-foreground mb-2">Sending</p>
                <p className="text-5xl font-bold balance-text mb-4">{formatCurrency(parseFloat(amount))}</p>
                <p className="text-muted-foreground">to</p>
                <div className="flex items-center justify-center gap-3 mt-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    {transferType === 'lekeewallet' && <User className="w-6 h-6 text-primary" />}
                    {transferType === 'bank' && <Building className="w-6 h-6 text-primary" />}
                    {transferType === 'mobile' && <Smartphone className="w-6 h-6 text-primary" />}
                  </div>
                  <div className="text-left">
                    <p className="font-medium">
                      {transferType === 'lekeewallet' && recipientId}
                      {transferType === 'bank' && accountName}
                      {transferType === 'mobile' && phoneNumber}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {transferType === 'lekeewallet' && 'LekeeWallet'}
                      {transferType === 'bank' && banks.find(b => b.code === selectedBank)?.name}
                      {transferType === 'mobile' && mobileProviders.find(p => p.code === selectedProvider)?.name}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-white/10 rounded-xl p-4 mb-6">
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Amount</span>
                  <span>{formatCurrency(parseFloat(amount))}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Fee</span>
                  <span>{formatCurrency(getFee())}</span>
                </div>
                <div className="flex justify-between py-2 font-medium border-t border-white/10 mt-2 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency((parseFloat(amount) || 0) + getFee())}</span>
                </div>
              </div>
              
              <Button
                onClick={() => setStep(3)}
                className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
                data-testid="proceed-to-pin"
              >
                Proceed to PIN
              </Button>
            </div>
          )}

          {/* Step 3: PIN Entry */}
          {step === 3 && (
            <div className="max-w-md mx-auto text-center">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Enter your PIN
              </h2>
              <p className="text-muted-foreground mb-8">Enter your 4-digit PIN to confirm</p>
              
              {/* PIN dots */}
              <div className="flex items-center justify-center gap-4 mb-6">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full transition-all ${
                      pin.length > i ? 'bg-primary scale-110' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>
              
              <NumberPad />
              
              {pin.length === 4 && (
                <Button
                  onClick={handleSubmit}
                  className="w-full h-12 mt-6 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
                  disabled={isLoading}
                  data-testid="confirm-transfer"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm and Send'}
                </Button>
              )}
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="max-w-md mx-auto text-center">
              <div className="py-12">
                <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Money Sent!
                </h2>
                <p className="text-muted-foreground">
                  Your transfer is being processed
                </p>
              </div>
              
              <div className="bg-card border border-white/10 rounded-xl p-6 mb-6">
                <p className="text-4xl font-bold balance-text mb-2">{formatCurrency(parseFloat(amount))}</p>
                <p className="text-muted-foreground">
                  to {transferType === 'lekeewallet' ? recipientId : (accountName || phoneNumber)}
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/home')}
                  className="flex-1 h-12 bg-primary hover:bg-red-600 text-white font-semibold"
                  data-testid="back-to-home"
                >
                  Back to Home
                </Button>
                <Button
                  onClick={() => {
                    setStep(1);
                    setAmount('');
                    setPin('');
                    setNote('');
                  }}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  Send Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </AppLayout>
    );
  }

  // Mobile Layout (existing)
  return (
    <AppLayout>
      <div className="min-h-screen pb-4">
        {/* Header */}
        <div className="p-6 flex items-center gap-4">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Send Money
          </h1>
        </div>
        
        {/* Progress */}
        {step < 4 && (
          <div className="px-6 mb-6">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    s <= step ? 'bg-primary' : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Step 1: Select Recipient */}
        {step === 1 && (
          <div className="px-6 fade-in">
            <Tabs value={transferType} onValueChange={setTransferType}>
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="lekeewallet">LekeeWallet</TabsTrigger>
                <TabsTrigger value="bank">Bank</TabsTrigger>
                <TabsTrigger value="mobile">Mobile</TabsTrigger>
              </TabsList>
              
              <TabsContent value="lekeewallet" className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Enter LekeeWallet ID, phone, or email"
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                    className="pl-12 h-12 bg-secondary border-white/10"
                    data-testid="recipient-input"
                  />
                </div>
                
                {beneficiaries.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Recent</p>
                    <div className="flex gap-4 overflow-x-auto hide-scrollbar -mx-6 px-6">
                      {beneficiaries.map((b, idx) => (
                        <button
                          key={idx}
                          onClick={() => setRecipientId(b.name)}
                          className="flex flex-col items-center gap-2 min-w-[80px]"
                        >
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                          <span className="text-xs text-center truncate w-full">{b.name.split(' ')[0]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="bank" className="space-y-4">
                <div className="space-y-2">
                  <Label>Bank</Label>
                  <Select value={selectedBank} onValueChange={setSelectedBank}>
                    <SelectTrigger className="h-12 bg-secondary border-white/10">
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.code} value={bank.code}>
                          {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    placeholder="Enter 10-digit account number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onBlur={resolveAccount}
                    className="h-12 bg-secondary border-white/10"
                    maxLength={10}
                    data-testid="bank-account-input"
                  />
                </div>
                
                {accountName && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="font-medium">{accountName}</span>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="mobile" className="space-y-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                    <SelectTrigger className="h-12 bg-secondary border-white/10">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {mobileProviders.map((provider) => (
                        <SelectItem key={provider.code} value={provider.code}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    placeholder="+234 800 000 0000"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="h-12 bg-secondary border-white/10"
                    data-testid="mobile-phone-input"
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <Button
              onClick={() => setStep(2)}
              className="w-full h-12 mt-6 bg-primary hover:bg-red-600 text-white font-semibold"
              disabled={!canProceedToAmount()}
              data-testid="continue-to-amount"
            >
              Continue
            </Button>
          </div>
        )}
        
        {/* Step 2: Enter Amount (Mobile) */}
        {step === 2 && (
          <div className="px-6 fade-in">
            {/* Recipient summary */}
            <div className="bg-card border border-white/10 rounded-xl p-4 mb-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                {transferType === 'lekeewallet' && <User className="w-6 h-6 text-primary" />}
                {transferType === 'bank' && <Building className="w-6 h-6 text-primary" />}
                {transferType === 'mobile' && <Smartphone className="w-6 h-6 text-primary" />}
              </div>
              <div>
                <p className="font-medium">
                  {transferType === 'lekeewallet' && recipientId}
                  {transferType === 'bank' && accountName}
                  {transferType === 'mobile' && phoneNumber}
                </p>
                <p className="text-sm text-muted-foreground">
                  {transferType === 'lekeewallet' && 'LekeeWallet'}
                  {transferType === 'bank' && banks.find(b => b.code === selectedBank)?.name}
                  {transferType === 'mobile' && mobileProviders.find(p => p.code === selectedProvider)?.name}
                </p>
              </div>
            </div>
            
            {/* Balance */}
            <p className="text-sm text-muted-foreground text-center mb-2">
              Available: {formatCurrency(wallet?.available_balance)}
            </p>
            
            {/* Amount input */}
            <div className="text-center mb-6">
              <span className="text-muted-foreground text-2xl">₦</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="0.00"
                className="text-5xl font-bold bg-transparent border-none outline-none text-center w-full balance-text"
                data-testid="amount-input"
              />
            </div>
            
            {/* Quick amounts */}
            <div className="flex gap-2 justify-center mb-6">
              {[1000, 5000, 10000, 50000].map((val) => (
                <button
                  key={val}
                  onClick={() => setAmount(String(val))}
                  className="px-4 py-2 bg-secondary rounded-lg text-sm hover:bg-white/10 transition-colors"
                >
                  ₦{val.toLocaleString()}
                </button>
              ))}
            </div>
            
            {/* Fee display */}
            {amount && (
              <div className="bg-card border border-white/10 rounded-xl p-4 mb-6 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span>{formatCurrency(parseFloat(amount) || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fee</span>
                  <span>{formatCurrency(getFee())}</span>
                </div>
                <div className="flex justify-between font-medium pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span>{formatCurrency((parseFloat(amount) || 0) + getFee())}</span>
                </div>
              </div>
            )}
            
            {/* Note */}
            <div className="space-y-2 mb-6">
              <Label>Note (optional)</Label>
              <Input
                placeholder="Add a note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-12 bg-secondary border-white/10"
              />
            </div>
            
            <Button
              onClick={() => setStep(3)}
              className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold"
              disabled={!amount || parseFloat(amount) <= 0 || parseFloat(amount) + getFee() > (wallet?.available_balance || 0)}
              data-testid="continue-to-confirm"
            >
              Continue
            </Button>
          </div>
        )}
        
        {/* Step 3: Confirm with PIN (Mobile) */}
        {step === 3 && (
          <div className="px-6 fade-in">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Confirm Transfer
              </h2>
              <p className="text-muted-foreground">Enter your PIN to send</p>
            </div>
            
            {/* Summary */}
            <div className="bg-card border border-white/10 rounded-xl p-6 mb-8 text-center">
              <p className="text-muted-foreground mb-2">Sending</p>
              <p className="text-4xl font-bold balance-text mb-4">{formatCurrency(parseFloat(amount))}</p>
              <p className="text-muted-foreground">to</p>
              <p className="font-medium mt-2">
                {transferType === 'lekeewallet' && recipientId}
                {transferType === 'bank' && accountName}
                {transferType === 'mobile' && phoneNumber}
              </p>
            </div>
            
            {/* PIN dots */}
            <div className="flex items-center justify-center gap-4 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-all ${
                    pin.length > i ? 'bg-primary scale-110' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            
            <NumberPad />
            
            {pin.length === 4 && (
              <Button
                onClick={handleSubmit}
                className="w-full h-12 mt-6 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
                disabled={isLoading}
                data-testid="confirm-transfer"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm and Send'}
              </Button>
            )}
          </div>
        )}
        
        {/* Step 4: Success (Mobile) */}
        {step === 4 && (
          <div className="px-6 fade-in text-center">
            <div className="py-12">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Money Sent!
              </h2>
              <p className="text-muted-foreground">
                Your transfer is being processed
              </p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-6 mb-6">
              <p className="text-4xl font-bold balance-text mb-2">{formatCurrency(parseFloat(amount))}</p>
              <p className="text-muted-foreground">
                to {transferType === 'lekeewallet' ? recipientId : (accountName || phoneNumber)}
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/home')}
                className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold"
                data-testid="back-to-home"
              >
                Back to Home
              </Button>
              <Button
                onClick={() => {
                  setStep(1);
                  setAmount('');
                  setPin('');
                  setNote('');
                }}
                variant="outline"
                className="w-full h-12"
              >
                Send Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default SendMoneyPage;
